import {
    Component,
    AfterViewInit,
    ViewChild,
    ChangeDetectorRef,
    inject,
    ChangeDetectionStrategy,
} from '@angular/core';
import {
    DxDataGridComponent,
    DxDateBoxComponent,
    DxFileUploaderComponent,
} from 'devextreme-angular';
import {
    values as _values,
    get as _get,
    set as _set,
    map as _map,
    isFinite as _isFinite,
    toNumber as _toNumber,
    toInteger as _toInteger,
    filter as _filter,
    find as _find,
    chunk as _chunk,
    trim as _trim,
} from 'lodash';
import { InventoryXlsxReaderService } from '../../services/inventory-xlsx-reader/inventory-xlsx-reader.service';
import {
    InventoryItem,
    InventoryReport,
} from '../../services/inventory-xlsx-reader/interfaces/inventory-report';
import DataSource from 'devextreme/data/data_source';
import ArrayStore from 'devextreme/data/array_store';
import { DataRow } from './interfaces/data-row';
import {
    DxFileUploaderTypes,
    DxFileUploaderComponent as DxFileUploaderComponent_1,
} from 'devextreme-angular/ui/file-uploader';
import { RequestTaxInvoiceService } from '../../services/request-tax-invoice/request-tax-invoice.service';
import {
    MessageType,
    NotificationService,
    UtilService,
} from 'src/app/shared/services';
import { concatMap, map } from 'rxjs/operators';
import { forkJoin, from } from 'rxjs';
import { Store } from 'devextreme/data';
import moment from 'moment';
import { Format } from 'devextreme/localization';
import { CreateFormRequest } from '../../services/request-tax-invoice/interfaces/api';
import { DxButtonComponent } from 'devextreme-angular/ui/button';
import { DxNumberBoxComponent } from 'devextreme-angular/ui/number-box';
import { DxDateBoxComponent as DxDateBoxComponent_1 } from 'devextreme-angular/ui/date-box';
import { DxBoxComponent } from 'devextreme-angular/ui/box';
import {
    DxiItemComponent,
    DxoFilterRowComponent,
    DxoEditingComponent,
    DxoSelectionComponent,
    DxiColumnComponent,
} from 'devextreme-angular/ui/nested';
import { DxTextBoxComponent } from 'devextreme-angular/ui/text-box';
import { DxDataGridComponent as DxDataGridComponent_1 } from 'devextreme-angular/ui/data-grid';
import { DxTemplateDirective } from 'devextreme-angular/core';
import { DxPopupComponent } from 'devextreme-angular/ui/popup';
import { FormatNumberPipe } from '../../../../shared/pipes/format-number.pipe';

@Component({
    selector: 'app-request-tax-invoice',
    templateUrl: './request-tax-invoice.component.html',
    styleUrls: ['./request-tax-invoice.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        DxButtonComponent,
        DxNumberBoxComponent,
        DxDateBoxComponent_1,
        DxBoxComponent,
        DxiItemComponent,
        DxTextBoxComponent,
        DxDataGridComponent_1,
        DxoFilterRowComponent,
        DxoEditingComponent,
        DxoSelectionComponent,
        DxiColumnComponent,
        DxTemplateDirective,
        DxPopupComponent,
        DxFileUploaderComponent_1,
        FormatNumberPipe,
    ],
})
export class RequestTaxInvoiceComponent implements AfterViewInit {
    private cdref = inject(ChangeDetectorRef);
    private utilService = inject(UtilService);
    private inventoryXlsxService = inject(InventoryXlsxReaderService);
    private requestTaxInvoiceService = inject(RequestTaxInvoiceService);
    private notificationService = inject(NotificationService);

    @ViewChild('dataGrid', { static: true })
    private dataGrid!: DxDataGridComponent;
    @ViewChild('fileSelector', { static: true })
    private fileSelector!: DxFileUploaderComponent;
    @ViewChild('requestDateBox', { static: true })
    requestDateBox!: DxDateBoxComponent;

    /** เพดานของ create-form (เทมเพลตมี 30 แถว ส่วนที่เกินถูกแทรกเพิ่มตอนสร้างไฟล์) */
    MAX_ITEM_SUPPORTED_PER_REPORT = 200;
    MAX_SKU_LISTED_IN_MESSAGE = 5;

    /** จำนวนสินค้าต่อ 1 ไฟล์ ผู้ใช้ปรับได้ในช่วง 1 ถึง MAX_ITEM_SUPPORTED_PER_REPORT */
    itemPerReport = 30;
    isDataLoading = false;
    taxInvoiceSelectDate: Date | null = null;
    inPriceDiscountPercent = 0.6;
    estimatedReport = 1;
    gridDataSource: DataSource | null = null;
    selectedRowKeys: string[] = [];
    createFormResults: any[] = [];
    selectedFileName: string | null = null;
    inPriceDiscountDisplayFormat: Format = {
        type: 'percent',
        precision: 0,
    };

    ngAfterViewInit(): void {
        this.taxInvoiceSelectDate = moment().startOf('month').toDate();
        this.cdref.detectChanges();
    }

    async onInventoryFileSelected(
        e: DxFileUploaderTypes.ValueChangedEvent,
    ): Promise<void> {
        if (this.fileSelector.isValid) {
            const selectedFile: globalThis.File | undefined = _get(
                e,
                'value[0]',
            );
            if (selectedFile) {
                this.selectedFileName = selectedFile.name;
                await this.loadInventoryFile(selectedFile);
            }
        } else {
            this.selectedFileName = null;
        }
    }
    onRowUpdating(e: any) {
        const updatePromise = new Promise((resolve) => {
            const sku: string = _get(e, 'key');
            const newShortName: string = _trim(
                _get(e, 'newData.productShortName'),
            );
            if (sku && newShortName) {
                this.requestTaxInvoiceService
                    .upsertShortName({
                        sku,
                        newProductShortName: newShortName,
                    })
                    .subscribe({
                        next: (res) => {
                            if (res === true) {
                                _set(
                                    e,
                                    'newData.productShortName',
                                    newShortName,
                                );
                                this.notificationService.pushMessage({
                                    displayTime: 3000,
                                    message: `${sku} updated`,
                                    messageType: MessageType.success,
                                });
                                resolve(false);
                            } else {
                                // SKU ไม่มีในคลังสินค้าของ crawler จึงบันทึกถาวรไม่ได้
                                // แต่ยังเก็บค่าที่พิมพ์ไว้ในตาราง ไม่งั้นสินค้าพวกนี้
                                // จะใส่ชื่อย่อไม่ได้เลย และฟอร์มจะไม่มีชื่อสินค้า
                                this.notificationService.pushMessage({
                                    displayTime: 6000,
                                    message: `${sku} ใช้ในฟอร์มนี้ได้ แต่บันทึกลงฐานข้อมูลไม่ได้ (ไม่พบ SKU ในคลังสินค้า)`,
                                    messageType: MessageType.warning,
                                });
                                resolve(false);
                            }
                        },
                        error: (err) => {
                            // เก็บค่าที่พิมพ์ไว้เช่นกัน ผู้ใช้จะได้สร้างฟอร์มต่อได้
                            // แม้การบันทึกจะล้มเหลว
                            this.notificationService.pushMessage({
                                displayTime: 6000,
                                message: `${sku} บันทึกลงฐานข้อมูลไม่สำเร็จ (${this.utilService.errorToString(
                                    err,
                                )}) แต่ยังใช้ในฟอร์มนี้ได้`,
                                messageType: MessageType.error,
                            });
                            resolve(false);
                        },
                    });
            } else {
                resolve(true);
            }
        });
        e.cancel = updatePromise;
    }

    removeSelectedFile(): void {
        this.fileSelector.instance.clear();
        this.selectedFileName = null;
        this.gridDataSource = null;
        this.selectedRowKeys = [];
        this.createFormResults = [];
        this.cdref.detectChanges();
    }

    fillAutoData(): void {
        const selectedRowKeys: string[] =
            this.dataGrid.instance.getSelectedRowKeys();
        if (selectedRowKeys.length > 0) {
            this.dataGridBeginLoading();
            this.requestTaxInvoiceService
                .getProductData({
                    skus: selectedRowKeys,
                })
                .pipe(
                    concatMap((resProductObj: any) => {
                        return from(_values(resProductObj));
                    }),
                    concatMap((resProduct) => {
                        const store: Store = this.gridDataSource!.store();

                        return Promise.all([
                            store.byKey(_get(resProduct, 'sku')),
                            resProduct,
                            store,
                        ]);
                    }),
                    map((e) => {
                        const currentValue: DataRow = e[0];
                        const resProduct = e[1];
                        const store: Store<DataRow, string> = e[2];
                        const sku: string = _get(resProduct, 'sku');

                        const outPriceRaw: number | null = _get(
                            resProduct,
                            'price',
                            null,
                        );
                        const outPrice = _toNumber(outPriceRaw);
                        const inPrice: number | null = _isFinite(outPrice)
                            ? this.calculateDiscountPrice(outPrice)
                            : null;

                        // ถ้าฐานข้อมูลยังไม่มีชื่อย่อ ให้คงค่าที่ผู้ใช้พิมพ์เองไว้
                        // ไม่งั้นการกด "เติมข้อมูล" ซ้ำ (เช่นหลังแก้ %ลด)
                        // จะล้างชื่อที่พิมพ์ไปแล้วทิ้งทั้งหมด
                        const productShortName: string | null =
                            _get(resProduct, 'pname_shortened', null) ??
                            _get(currentValue, 'productShortName', null);

                        return store.update(sku, {
                            no: _get(currentValue, 'no', null),
                            productName: _get(
                                currentValue,
                                'productName',
                                null,
                            ),
                            productShortName: productShortName,
                            toOrderQuantity: _get(
                                currentValue,
                                'toOrderQuantity',
                                null,
                            ),
                            sku: sku,
                            inPrice: inPrice,
                            outPrice: outPriceRaw,
                            thumbnail: _get(resProduct, 'thumbnail', null),
                        });
                    }),
                )
                .subscribe(
                    () => {
                        this.dataGridEndLoading();
                    },
                    (error) => {
                        console.error(error);
                        this.notificationService.pushMessage({
                            displayTime: 6000,
                            message: this.utilService.errorToString(error),
                            messageType: MessageType.error,
                        });
                        this.dataGridEndLoading();
                    },
                    () => {
                        this.gridDataSource!.reload();
                    },
                );
        }
    }

    async createForm(): Promise<void> {
        const allDataRow: DataRow[] =
            (await this.gridDataSource!.store().load()) as DataRow[];
        const selectedRows = _map(this.selectedRowKeys, (sku: string) => {
            return {
                sku,
                dataRow: _find(allDataRow, { sku: sku }),
            };
        });

        // ชื่อสินค้าและราคาในฟอร์มมาจาก productShortName/inPrice ซึ่งเติมได้จากปุ่ม
        // "เติมข้อมูล" เท่านั้น ถ้าปล่อยว่างไว้ฝั่ง backend จะเขียนชื่อเป็นค่าว่าง
        // และราคาเป็น 0 ให้เงียบ ๆ จึงต้องกันไม่ให้สร้างฟอร์มที่ใช้งานไม่ได้
        const incompleteSkus: string[] = _map(
            _filter(
                selectedRows,
                (row) => !this.isRowReadyForForm(row.dataRow),
            ),
            (row) => row.sku,
        );
        if (incompleteSkus.length > 0) {
            this.notifyIncompleteRows(incompleteSkus);
            return;
        }

        const productItems = _map(selectedRows, ({ dataRow }) => {
            return {
                number: dataRow!.no,
                sku: dataRow!.sku,
                price: dataRow!.inPrice,
                product_name: dataRow!.productShortName,
                quantity: dataRow!.toOrderQuantity,
                imgUrl: dataRow!.thumbnail,
            };
        });

        if (productItems.length > 0) {
            const taxDate: moment.Moment = moment(this.taxInvoiceSelectDate);
            taxDate.locale('th');
            if (taxDate.isValid()) {
                const productItemChunks: any[] = _chunk(
                    productItems,
                    this.resolveItemPerReport(),
                );
                const requestObservables = _map(
                    productItemChunks,
                    (productItemChunk, i) => {
                        const request: CreateFormRequest = {
                            form: {
                                fileName: `ฟอร์มขอใบกำกับภาษี ${taxDate.format(
                                    'D-MM-YYYY',
                                )} [${i + 1}]`,
                                dateStrD: taxDate.format('DD'),
                                dateStrM: taxDate.format('MMMM'),
                                dateStrY: (taxDate.year() + 543).toString(),
                            },
                            products: productItemChunk,
                        };
                        return this.requestTaxInvoiceService.createForm(
                            request,
                        );
                    },
                );
                this.createFormResults = [];
                this.dataGridBeginLoading();
                forkJoin(requestObservables).subscribe(
                    (results: any[]) => {
                        this.createFormResults = results;
                        this.dataGridEndLoading();
                        this.notificationService.pushMessage({
                            displayTime: 3000,
                            message: 'สร้างไฟล์สำเร็จ',
                            messageType: MessageType.success,
                        });
                    },
                    (error) => {
                        this.dataGridEndLoading();
                        console.error(error);
                        this.notificationService.pushMessage({
                            displayTime: 6000,
                            message: this.utilService.errorToString(error),
                            messageType: MessageType.error,
                        });
                    },
                );
            }
        }
    }

    openSharedUrl(sharedUrl: string): void {
        if (sharedUrl) {
            window.open(sharedUrl, '_blank');
        }
    }

    copySharedUrlToClibboard(sharedUrl: string): void {
        if (sharedUrl) {
            navigator.clipboard.writeText(sharedUrl).then(
                () => {
                    this.notificationService.pushMessage({
                        message: 'URL copied to clipboard.',
                        messageType: MessageType.success,
                        displayTime: 100,
                    });
                },
                (e) => {
                    console.error(e);
                },
            );
        }
    }

    /** ระบายสีช่องที่ยังเติมข้อมูลไม่ครบ เพื่อให้เห็นก่อนกดสร้างฟอร์ม */
    onCellPrepared(e: any): void {
        if (_get(e, 'rowType') !== 'data') {
            return;
        }
        const dataField: string = _get(e, 'column.dataField', '');
        const dataRow: DataRow | undefined = _get(e, 'data');
        const isMissing: boolean =
            (dataField === 'productShortName' &&
                !this.isShortNameFilled(dataRow?.productShortName)) ||
            (dataField === 'inPrice' &&
                !this.isInPriceFilled(dataRow?.inPrice));
        if (isMissing) {
            e.cellElement.classList.add('missing-form-data');
        }
    }

    onDataGridSelectionChanged(): void {
        this.updateEstimatedReport();
    }

    onItemPerReportChanged(): void {
        this.updateEstimatedReport();
    }

    private updateEstimatedReport(): void {
        this.estimatedReport = Math.ceil(
            this.selectedRowKeys.length / this.resolveItemPerReport(),
        );
        this.cdref.detectChanges();
    }

    /** กันค่าที่ผู้ใช้พิมพ์เอง (ว่าง ทศนิยม ติดลบ หรือเกินเพดานของเทมเพลต) */
    private resolveItemPerReport(): number {
        const requested: number = _toInteger(this.itemPerReport);
        if (requested < 1) {
            return 1;
        }
        if (requested > this.MAX_ITEM_SUPPORTED_PER_REPORT) {
            return this.MAX_ITEM_SUPPORTED_PER_REPORT;
        }
        return requested;
    }

    private isShortNameFilled(
        productShortName: string | null | undefined,
    ): boolean {
        return _trim(productShortName ?? '') !== '';
    }

    private isInPriceFilled(inPrice: number | null | undefined): boolean {
        return _isFinite(inPrice) && inPrice! > 0;
    }

    /** แถวจะส่งเข้าฟอร์มได้ต่อเมื่อมีทั้งชื่อสินค้า (ย่อ) และราคาซื้อ */
    private isRowReadyForForm(dataRow: DataRow | undefined): boolean {
        return (
            !!dataRow &&
            this.isShortNameFilled(dataRow.productShortName) &&
            this.isInPriceFilled(dataRow.inPrice)
        );
    }

    private notifyIncompleteRows(incompleteSkus: string[]): void {
        const listedSkus: string[] = incompleteSkus.slice(
            0,
            this.MAX_SKU_LISTED_IN_MESSAGE,
        );
        const ellipsis: string =
            incompleteSkus.length > listedSkus.length ? ', ...' : '';
        this.notificationService.pushMessage({
            displayTime: 8000,
            messageType: MessageType.error,
            message:
                `ยังไม่มีชื่อสินค้า (ย่อ) หรือราคาซื้อ ${incompleteSkus.length} รายการ ` +
                `(${listedSkus.join(', ')}${ellipsis}) — กด "เติมข้อมูล" ก่อนสร้างฟอร์ม`,
        });
    }

    private calculateDiscountPrice(norminalPrice: number): number {
        return Math.ceil(norminalPrice * this.inPriceDiscountPercent);
    }

    private dataGridBeginLoading(): void {
        this.dataGrid.instance.beginCustomLoading('');
        this.isDataLoading = true;
        this.cdref.detectChanges();
    }

    private dataGridEndLoading(): void {
        this.dataGrid.instance.endCustomLoading();
        this.isDataLoading = false;
        this.cdref.detectChanges();
    }

    private async loadInventoryFile(
        inventoryFile: globalThis.File,
    ): Promise<void> {
        const report: InventoryReport =
            await this.inventoryXlsxService.read(inventoryFile);
        report.inventoryItems = _filter(
            report.inventoryItems,
            (item: InventoryItem) => {
                return item.onHand < 0;
            },
        );
        this.gridDataSource = new DataSource<DataRow, string>({
            store: new ArrayStore<DataRow, string>({
                key: 'sku',
                data: _map(report.inventoryItems, (item): DataRow => {
                    return {
                        no: item.orderNo,
                        productName: item.productName,
                        productShortName: null,
                        sku: item.sku,
                        inPrice: null,
                        outPrice: null,
                        toOrderQuantity: Math.abs(item.onHand),
                        thumbnail: null,
                    };
                }),
            }),
        });
        this.cdref.detectChanges();
        await this.dataGrid.instance.selectAll();
    }
}
